import React, {useEffect, useRef, useState} from 'react';
import Chart from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import CellButton from '../../../common/components/resource-table/table-components/cell-button';
import {checkPerm} from '../../../util/util';
import {READ_PERM} from '../../../util/util-constants';
import Resources from '../../../data/services/resources';
import {getHeatColor} from "../../../common/util/util-formaters";

Chart.register(annotationPlugin);

const fmt$ = (v) => `$${Number(v ?? 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
const fmtMi = (v) => Number(v ?? 0).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0});
const fmtCpm = (v) => `$${Number(v ?? 0).toFixed(2)}`;

const shortLabel = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {month: 'short', day: 'numeric'});
};

const fullLabel = (p) =>
    `${shortLabel(p.PeriodStart)} – ${shortLabel(p.PeriodEnd)}`;

const TrendChart = ({periods}) => {
    const canvasRef = useRef(null);
    const chartRef = useRef(null);

    useEffect(() => {
        if (!canvasRef.current || !periods?.length) return;
        if (chartRef.current) chartRef.current.destroy();

        // Sort oldest → newest (highest PeriodIndex first)
        const sorted = [...periods].sort((a, b) => b.PeriodIndex - a.PeriodIndex);

        const styles = getComputedStyle(document.documentElement);
        const primaryColor = styles.getPropertyValue('--color-primary').trim() || '#4f46e5';

        const avgCpm = sorted.reduce((s, p) => s + (p.CostPerMile ?? 0), 0) / sorted.length;

        const ctx = canvasRef.current.getContext('2d');
        chartRef.current = new Chart(ctx, {
            data: {
                labels: sorted.map(p => shortLabel(p.PeriodStart)),
                datasets: [
                    {
                        type: 'bar',
                        label: 'Total Amount ($)',
                        data: sorted.map(p => p.TotalAmount ?? 0),
                        backgroundColor: '#60a5fa55',
                        borderColor: '#3b82f6',
                        borderWidth: 1,
                        borderRadius: 3,
                        yAxisID: 'yAmount',
                        order: 2,
                    },
                    {
                        type: 'bar',
                        label: 'Total Mileage (mi)',
                        data: sorted.map(p => p.TotalMileage ?? 0),
                        backgroundColor: '#34d39933',
                        borderColor: '#10b981',
                        borderWidth: 1,
                        borderRadius: 3,
                        yAxisID: 'yMileage',
                        order: 3,
                    },
                    {
                        type: 'line',
                        label: 'Cost per Mile ($/mi)',
                        data: sorted.map(p => p.CostPerMile ?? 0),
                        borderColor: primaryColor,
                        backgroundColor: primaryColor + '18',
                        borderWidth: 2.5,
                        pointRadius: sorted.map(p => p.PeriodIndex === 0 ? 6 : 4),
                        pointBackgroundColor: sorted.map(p => p.PeriodIndex === 0 ? primaryColor : 'transparent'),
                        pointBorderColor: primaryColor,
                        pointBorderWidth: 2,
                        pointHoverRadius: 7,
                        tension: 0.3,
                        fill: false,
                        yAxisID: 'yCpm',
                        order: 1,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {intersect: false, mode: 'index'},
                plugins: {
                    legend: {
                        display: true,
                        position: 'top',
                        labels: {font: {size: 11}, boxWidth: 12, padding: 14},
                    },
                    tooltip: {
                        backgroundColor: '#fff',
                        titleColor: '#111827',
                        bodyColor: '#4b5563',
                        borderColor: '#d1d5db',
                        borderWidth: 1,
                        padding: 12,
                        cornerRadius: 8,
                        titleFont: {weight: '600', size: 13},
                        bodyFont: {size: 12},
                        displayColors: true,
                        callbacks: {
                            title: (items) => fullLabel(sorted[items[0].dataIndex]),
                            label: (item) => {
                                const p = sorted[item.dataIndex];
                                if (item.datasetIndex === 0) return ` Amount: ${fmt$(p.TotalAmount)}`;
                                if (item.datasetIndex === 1) return ` Mileage: ${fmtMi(p.TotalMileage)} mi`;
                                return ` Cost/Mile: ${fmtCpm(p.CostPerMile)}`;
                            },
                        },
                    },
                    annotation: {
                        annotations: {
                            avgLine: {
                                type: 'line',
                                yMin: avgCpm,
                                yMax: avgCpm,
                                yScaleID: 'yCpm',
                                borderColor: '#9ca3af',
                                borderWidth: 1,
                                borderDash: [4, 4],
                                label: {
                                    display: true,
                                    content: `Avg ${fmtCpm(avgCpm)}/mi`,
                                    position: 'end',
                                    backgroundColor: 'transparent',
                                    color: '#9ca3af',
                                    font: {size: 11},
                                },
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        grid: {display: false},
                        ticks: {color: '#6b7280', font: {size: 11}},
                        border: {color: '#e5e7eb'},
                    },
                    yAmount: {
                        type: 'linear',
                        position: 'left',
                        grid: {color: '#f3f4f6'},
                        ticks: {
                            color: '#3b82f6',
                            font: {size: 11},
                            callback: (v) => `$${v.toLocaleString()}`,
                        },
                        border: {display: false},
                        title: {display: true, text: 'Amount ($)', color: '#3b82f6', font: {size: 11}},
                    },
                    yMileage: {
                        type: 'linear',
                        position: 'right',
                        grid: {drawOnChartArea: false},
                        ticks: {
                            color: '#10b981',
                            font: {size: 11},
                            callback: (v) => `${v.toLocaleString()} mi`,
                        },
                        border: {display: false},
                        title: {display: true, text: 'Mileage (mi)', color: '#10b981', font: {size: 11}},
                    },
                    yCpm: {
                        type: 'linear',
                        position: 'right',
                        grid: {drawOnChartArea: false},
                        ticks: {
                            color: '#6b7280',
                            font: {size: 11},
                            callback: (v) => `$${v.toFixed(2)}`,
                        },
                        border: {display: false},
                        title: {display: true, text: '$/mile', color: '#6b7280', font: {size: 11}},
                    },
                },
            },
        });

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
    }, [periods]);

    return <canvas ref={canvasRef}/>;
};

export default function CostPerMileTrend({periods, list, count, isLoading, isTrailerMode, translate, onViewTruckClick, onViewTrailerClick, onUnitClick}) {
    const [truckSearch, setTruckSearch] = useState('')
    const [yearFilter, setYearFilter] = useState('')
    const [manufacturerFilter, setManufacturerFilter] = useState('')
    const [modelFilter, setModelFilter] = useState('')
    const [sortCol, setSortCol] = useState('TotalAmount')
    const [sortDir, setSortDir] = useState('desc')

    const handleSortChange = (col) => {
        if (col === sortCol) {
            setSortDir(prev => prev === 'asc' ? 'desc' : 'asc')
        } else {
            setSortCol(col)
            setSortDir('desc')
        }
    }

    const SortIcon = ({col}) => {
        if (sortCol !== col) return <span className="ml-1 text-tm-gray-300">↕</span>
        return <span className="ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"/>
            </div>
        );
    }

    if (!periods?.length && !list?.length) return null;

    const sorted = [...(periods ?? [])].sort((a, b) => b.PeriodIndex - a.PeriodIndex);
    const currentPeriod = (periods ?? []).find(p => p.PeriodIndex === 0);

    return (
        <div className="space-y-6 py-4">
            {/* Chart */}
            <div className="md:bg-inverse md:border border-tm-gray-300 md:shadow-card rounded-card">
                <div className="px-4 pt-3 pb-1">
                    <p className="text-xs font-semibold text-tm-gray-500 uppercase tracking-wide">
                        Cost Per Mile Trend
                    </p>
                </div>
                <div className="p-4" style={{height: 320}}>
                    {sorted.length ? (
                        <TrendChart periods={sorted}/>
                    ) : (
                        <div className="flex items-center justify-center h-full text-sm text-tm-gray-400">No
                            data</div>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
                {/* Period Breakdown Table */}
                <div className="lg:col-span-3">
                    <p className="text-xs font-semibold text-tm-gray-500 uppercase tracking-wide mb-2 px-1">
                        Period Breakdown
                    </p>
                    <div
                        className="md:bg-inverse md:border border-tm-gray-300 md:shadow-card rounded-card overflow-auto">
                        <table className="min-w-full text-sm divide-y divide-tm-gray-300">
                            <thead className="bg-tm-gray-50">
                            <tr>
                                <th className="px-4 py-2.5 text-left font-medium text-tm-gray-700 whitespace-nowrap">Period</th>
                                <th className="px-4 py-2.5 text-right font-medium text-tm-gray-700 whitespace-nowrap">Mileage</th>
                                <th className="px-4 py-2.5 text-right font-medium text-tm-gray-700 whitespace-nowrap">Amount</th>
                                <th className="px-4 py-2.5 text-right font-medium text-tm-gray-700 whitespace-nowrap">Cost/Mile</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-tm-gray-200">
                            {sorted.map((p) => {
                                const isCurrent = p.PeriodIndex === 0;
                                return (
                                    <tr key={p.PeriodIndex}
                                        className={isCurrent ? 'bg-primary/5' : 'bg-popup'}>
                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                            <span className="text-tm-gray-700 text-xs">
                                                {shortLabel(p.PeriodStart)} – {shortLabel(p.PeriodEnd)}
                                            </span>
                                            {isCurrent && (
                                                <span
                                                    className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-primary">
                                                    Current
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-tm-gray-700 text-xs">
                                            {fmtMi(p.TotalMileage)} mi
                                        </td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-tm-gray-700 text-xs font-medium">
                                            {fmt$(p.TotalAmount)}
                                        </td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-xs font-semibold">
                                            {p.CostPerMile > 0
                                                ? <span className="text-primary">{fmtCpm(p.CostPerMile)}/mi</span>
                                                : <span className="text-tm-gray-400">—</span>
                                            }
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Current Period Summary */}
                <div className="lg:col-span-2">
                    <p className="text-xs font-semibold text-tm-gray-500 uppercase tracking-wide mb-2 px-1">
                        Current Period Summary
                    </p>
                    {currentPeriod ? (
                        <div
                            className="md:bg-inverse md:border border-tm-gray-300 md:shadow-card rounded-card p-4 space-y-3">
                            <div className="text-xs text-tm-gray-400 mb-1">
                                {shortLabel(currentPeriod.PeriodStart)} – {shortLabel(currentPeriod.PeriodEnd)}
                            </div>
                            <div className="grid grid-cols-1 gap-3">
                                <div className="flex justify-between items-center py-2 border-b border-tm-gray-200">
                                    <span className="text-sm text-tm-gray-600">Total Mileage</span>
                                    <span
                                        className="font-semibold tabular-nums text-tm-gray-900">{fmtMi(currentPeriod.TotalMileage)} mi</span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-tm-gray-200">
                                    <span className="text-sm text-tm-gray-600">Total Amount</span>
                                    <span
                                        className="font-semibold tabular-nums text-blue-600">{fmt$(currentPeriod.TotalAmount)}</span>
                                </div>
                                <div className="flex justify-between items-center py-2">
                                    <span className="text-sm text-tm-gray-600">Cost per Mile</span>
                                    <span
                                        className="font-bold tabular-nums text-primary text-base">{fmtCpm(currentPeriod.CostPerMile)}/mi</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div
                            className="md:bg-inverse md:border border-tm-gray-300 md:shadow-card rounded-card p-6 text-center text-sm text-tm-gray-400">
                            No current period data
                        </div>
                    )}
                </div>
            </div>

            {/* Truck List */}
            {list?.length > 0 && (() => {
                const years = [...new Set(list.map(t => t.Year).filter(Boolean))].sort((a, b) => b - a)

                let maxAmount = 0, minAmount = 0, maxCpm = 0, minCpm = 0
                list.forEach(t => {
                    if (t.TotalAmount > maxAmount) maxAmount = t.TotalAmount
                    if (t.TotalAmount < minAmount) minAmount = t.TotalAmount
                    if (t.CostPerMile > maxCpm) maxCpm = t.CostPerMile
                    if (t.CostPerMile < minCpm) minCpm = t.CostPerMile
                })

                const filteredList = list.filter(t => {
                    if (truckSearch && ![t.Name, t.Year, t.Manufacturer, t.Model].some(v => String(v ?? '').toLowerCase().includes(truckSearch.toLowerCase()))) return false
                    if (yearFilter && String(t.Year ?? '') !== yearFilter) return false
                    if (manufacturerFilter && !String(t.Manufacturer ?? '').toLowerCase().includes(manufacturerFilter.toLowerCase())) return false
                    if (modelFilter && !String(t.Model ?? '').toLowerCase().includes(modelFilter.toLowerCase())) return false
                    return true
                }).sort((a, b) => {
                    const av = a[sortCol] ?? ''
                    const bv = b[sortCol] ?? ''
                    const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv))
                    return sortDir === 'asc' ? cmp : -cmp
                })

                const hasFilters = truckSearch || yearFilter || manufacturerFilter || modelFilter

                return (
                <div>
                    <div className="flex items-center justify-between mb-2 px-1">
                        <p className="text-xs font-semibold text-tm-gray-500 uppercase tracking-wide">
                            By {isTrailerMode ? 'Trailer' : 'Truck'} ({filteredList.length}{filteredList.length !== count ? ` of ${count}` : ''})
                        </p>
                        {hasFilters && (
                            <button
                                className="text-xs text-tm-gray-400 hover:text-tm-gray-700 mr-2"
                                onClick={() => { setTruckSearch(''); setYearFilter(''); setManufacturerFilter(''); setModelFilter('') }}
                            >
                                Clear filters
                            </button>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2 mb-2">
                        <input
                            type="search"
                            className="form-control w-44 text-sm"
                            placeholder="Search..."
                            value={truckSearch}
                            onChange={(e) => setTruckSearch(e.target.value)}
                        />
                        <select
                            className="form-control w-28 text-sm"
                            value={yearFilter}
                            onChange={(e) => setYearFilter(e.target.value)}
                        >
                            <option value="">All years</option>
                            {years.map(y => <option key={y} value={String(y)}>{y}</option>)}
                        </select>
                        <input
                            type="search"
                            className="form-control w-40 text-sm"
                            placeholder="Manufacturer..."
                            value={manufacturerFilter}
                            onChange={(e) => setManufacturerFilter(e.target.value)}
                        />
                        <input
                            type="search"
                            className="form-control w-36 text-sm"
                            placeholder="Model..."
                            value={modelFilter}
                            onChange={(e) => setModelFilter(e.target.value)}
                        />
                    </div>
                    <div
                        className="md:bg-inverse md:border border-tm-gray-300 md:shadow-card rounded-card overflow-auto">
                        <table className="min-w-full text-sm divide-y divide-tm-gray-300">
                            <thead className="bg-tm-gray-50">
                            <tr>
                                {[
                                    {col: 'Name', label: isTrailerMode ? 'Trailer' : 'Truck', align: 'left'},
                                    {col: 'Year', label: 'Year', align: 'left'},
                                    {col: 'Manufacturer', label: 'Manufacturer', align: 'left'},
                                    {col: 'Model', label: 'Model', align: 'left'},
                                    {col: 'TotalMileage', label: 'Total Mileage', align: 'right'},
                                    {col: 'TotalAmount', label: 'Total Amount', align: 'right'},
                                    {col: 'CostPerMile', label: 'Cost/Mile', align: 'right'},
                                ].map(({col, label, align}) => (
                                    <th
                                        key={col}
                                        className={`px-4 py-2.5 text-${align} font-medium text-tm-gray-700 whitespace-nowrap cursor-pointer select-none hover:bg-tm-gray-100`}
                                        onClick={() => handleSortChange(col)}
                                    >
                                        {label}<SortIcon col={col}/>
                                    </th>
                                ))}
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-tm-gray-200">
                            {filteredList.map((truck) => (
                                <tr key={truck.TruckID} className="bg-popup hover:bg-tm-gray-50 transition-colors">
                                    <td className="px-4 py-2.5 font-medium">
                                        {!isTrailerMode && truck.TruckID ? (
                                            <CellButton onClick={() => onUnitClick?.(truck.TruckID, truck.Name)}>
                                                {truck.Name}
                                            </CellButton>
                                        ) : isTrailerMode && truck.TrailerID ? (
                                            <CellButton onClick={() => onUnitClick?.(truck.TrailerID, truck.Name)}>
                                                {truck.Name}
                                            </CellButton>
                                        ) : (
                                            <span className="text-tm-gray-700">{truck.Name}</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2.5 text-tm-gray-600 text-xs">
                                        {truck.Year || <span className="text-tm-gray-400">—</span>}
                                    </td>
                                    <td className="px-4 py-2.5 text-tm-gray-600 text-xs">
                                        {truck.Manufacturer || <span className="text-tm-gray-400">—</span>}
                                    </td>
                                    <td className="px-4 py-2.5 text-tm-gray-600 text-xs">
                                        {truck.Model || <span className="text-tm-gray-400">—</span>}
                                    </td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-tm-gray-600 text-xs">
                                        {truck.TotalMileage > 0 ? `${fmtMi(truck.TotalMileage)} mi` :
                                            <span className="text-tm-gray-400">—</span>}
                                    </td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-xs font-medium">
                                        {truck.TotalAmount > 0
                                            ? <span style={{color: getHeatColor(truck.TotalAmount, minAmount, maxAmount)}}>{fmt$(truck.TotalAmount)}</span>
                                            : <span className="text-tm-gray-400">—</span>}
                                    </td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-xs font-semibold">
                                        {truck.CostPerMile > 0
                                            ? <span style={{color: getHeatColor(truck.CostPerMile, minCpm, maxCpm)}}>{fmtCpm(truck.CostPerMile)}/mi</span>
                                            : <span className="text-tm-gray-400">—</span>
                                        }
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                            {filteredList.length > 0 && (() => {
                                const totMi = filteredList.reduce((s, t) => s + (t.TotalMileage ?? 0), 0);
                                const totAmt = filteredList.reduce((s, t) => s + (t.TotalAmount ?? 0), 0);
                                const totCpm = totMi > 0 ? totAmt / totMi : 0;
                                return (
                                    <tfoot>
                                    <tr className="bg-tm-gray-50 font-semibold border-t-2 border-tm-gray-300">
                                        <td colSpan={4} className="px-4 py-2.5 text-tm-gray-700">Total</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-tm-gray-700 text-xs">{fmtMi(totMi)} mi</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-tm-gray-700 text-xs">{fmt$(totAmt)}</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-xs text-tm-gray-700">
                                            {totCpm > 0 ? `${fmtCpm(totCpm)}/mi` : '—'}
                                        </td>
                                    </tr>
                                    </tfoot>
                                );
                            })()}
                        </table>
                    </div>
                </div>
                );
            })()}
        </div>
    );
}
